import { getLicenseType } from '@shared/constants/organization/LicenseUtils';
import { AllLicenseTypes } from '@shared/models/organizations/AllLicenseTypes';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AlertsContainer from './state/AlertsContainer';
import OrganizationContainer from './state/OrganizationContainer';
import useResolveCurrentOrg from './useResolveCurrentOrg';

export default function useCurrentOrgDetailsFetching() {
    const { t } = useTranslation();
    const { organizationId, managedOrgId } = useResolveCurrentOrg();
    const alertsContainer = AlertsContainer.useContainer();
    const {
        loadingDataRef,
        loadingData,
        handleLoadingDataChange,
        currentOrg,
        currentOrgRef,
        memberships,
        membershipsRef,
        getOrganizationMembership,
        loadCurrentOrganizationDetails,
    } = OrganizationContainer.useContainer();

    const fetchOrgDetailData = useCallback(async () => {
        if (!organizationId || currentOrgRef.current?.id == organizationId || loadingDataRef.current.currentOrg) {
            return;
        }
        try {
            handleLoadingDataChange('currentOrg', true);
            const result = await loadCurrentOrganizationDetails(organizationId);
            if (result.resultType != 'Ok') {
                throw new Error('Error fetching organization data');
            }
        } catch (error) {
            alertsContainer.showError(t('common:organization.error'));
        } finally {
            handleLoadingDataChange('currentOrg', false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    useEffect(() => {
        fetchOrgDetailData();
    }, [fetchOrgDetailData]);

    const remainingSeats = useMemo(() => {
        if (!currentOrg || currentOrg.currentMemberCount == null || !currentOrg.maxMembers) return 0;
        if (currentOrg.maxMembers < currentOrg.currentMemberCount) return 0;
        return currentOrg.maxMembers - currentOrg.currentMemberCount;
    }, [currentOrg]);

    const isExpiredOrg = useMemo(() => {
        if (!currentOrg || !currentOrg.licenseExpirationDate) return false;
        return currentOrg.licenseExpirationDate && new Date(currentOrg.licenseExpirationDate) < new Date();
    }, [currentOrg]);

    const licenseType: AllLicenseTypes = useMemo(() => {
        return getLicenseType(currentOrg);
    }, [currentOrg]);

    const currentMembership = useMemo(() => {
        if (!membershipsRef?.current || !organizationId) return null;
        return getOrganizationMembership(organizationId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId, memberships]);

    const originatingOrgMembership = useMemo(() => {
        if (!membershipsRef?.current || !currentOrg || !currentOrg.originatedByOrganizationId) return null;
        return getOrganizationMembership(currentOrg.originatedByOrganizationId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentOrg, memberships]);

    const membership = useMemo(() => {
        if (!organizationId) return null;
        if (currentMembership) return currentMembership;
        if (originatingOrgMembership) {
            const newMembership = {
                ...originatingOrgMembership,
            };
            newMembership.organizationId = organizationId;
            newMembership.isAdmin = true;
            return newMembership;
        }
        return null;
    }, [currentMembership, originatingOrgMembership, organizationId]);

    const hasSomeMemberEditPermissions = useMemo(() => {
        return !!(
            currentMembership?.isAdmin ||
            currentMembership?.canRemoveMembers ||
            currentMembership?.canChangeMemberPermissions ||
            originatingOrgMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations
        );
    }, [currentMembership, originatingOrgMembership]);

    const canManageBilling = useMemo(() => {
        return !!(
            currentMembership?.canManageBilling ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    //     canAccessOrganizationProfiles: boolean;
    // canEditOrganizationProfiles: boolean;
    const canAccessOrganizationProfiles = useMemo(() => {
        return !!(
            currentMembership?.canAccessOrganizationProfiles ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    const canEditOrganizationProfiles = useMemo(() => {
        return !!(
            currentMembership?.canEditOrganizationProfiles ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    const canInviteMembers = useMemo(() => {
        return !!(
            currentMembership?.canInviteMembers ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    const canViewMembers = useMemo(() => {
        return !!(
            currentMembership?.canViewMembers ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    const canCreateAuthClients = useMemo(() => {
        return !!(
            currentMembership?.canCreateAuthClients ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    const canViewAuthClients = useMemo(() => {
        return !!(
            currentMembership?.canViewAuthClients ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    const canManageChildOrganizations = useMemo(() => {
        return !!(
            currentMembership?.canManageChildOrganizations ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    const canViewOrgDetails = useMemo(() => {
        return !!(managedOrgId || (currentOrg?.maxMembers ?? 0) > 1);
    }, [currentOrg, managedOrgId]);

    const isManagedOrg = useMemo(() => {
        return !!(managedOrgId || currentOrg?.originatedByOrganizationId);
    }, [managedOrgId, currentOrg]);

    return {
        isLoading: loadingData.currentOrg,
        currentOrg,
        membership,
        remainingSeats,
        isExpiredOrg,
        licenseType,
        hasSomeMemberEditPermissions,
        canManageBilling,
        canCreateAuthClients,
        canViewAuthClients,
        canInviteMembers,
        canViewMembers,
        canManageChildOrganizations,
        canViewOrgDetails,
        canAccessOrganizationProfiles,
        canEditOrganizationProfiles,
        isManagedOrg,
    };
}
